import { motion } from "framer-motion";
import { CupSoda, Sparkles, Music, Users } from "lucide-react";
import { OWNER_SPACE_IMAGE, NEON_IMAGE, SNACKS_IMAGE, CONCRETE_LIGHT } from "../lib/api";
import { useContent } from "../lib/content";

const reveal = {
  hidden: { opacity: 0, y: 36 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const LoungeSection = () => {
  const { t } = useContent();

  const features = [
    { icon: CupSoda, title: t("feature_1_title"), text: t("feature_1_text") },
    { icon: Sparkles, title: t("feature_2_title"), text: t("feature_2_text") },
    { icon: Music, title: t("feature_3_title"), text: t("feature_3_text") },
    { icon: Users, title: t("feature_4_title"), text: t("feature_4_text") },
  ];

  return (
    <section
      id="lounge"
      className="concrete-section py-24 sm:py-32"
      style={{ backgroundImage: `url(${CONCRETE_LIGHT})` }}
    >
      <div className="relative max-w-6xl mx-auto px-6">
        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="font-editorial text-xl text-[#1d1d1b]"
        >
          {t("lounge_kicker")}
        </motion.p>
        <motion.h2
          variants={reveal}
          custom={1}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="font-display font-extrabold tracking-[-0.02em] text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-2xl leading-[1.12]"
        >
          {t("lounge_heading")}
        </motion.h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="card-soft md:col-span-7 overflow-hidden"
          >
            <div className="relative h-[380px]">
              <img
                src={t("lounge_space_image") || OWNER_SPACE_IMAGE}
                alt="Inside MAS Arts Studio — concrete walls, couches and neon art"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 glass-chip rounded-2xl p-5">
                <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#1d1d1b]">
                  the space · yes, this is really it
                </p>
                <h3 className="font-display font-extrabold text-lg mt-1 tracking-tight">{t("space_title")}</h3>
                <p className="text-xs text-neutral-500 mt-1.5 max-w-md leading-relaxed">{t("space_text")}</p>
              </div>
            </div>
          </motion.div>

          <div className="md:col-span-5 grid grid-rows-2 gap-6">
            <motion.div
              variants={reveal}
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="card-soft overflow-hidden"
            >
              <div className="relative h-[177px]">
                <img src={t("lounge_art_image") || NEON_IMAGE} alt="Neon artwork glowing on the studio wall" loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute bottom-3 left-3 right-3 glass-chip rounded-xl px-4 py-2.5">
                  <h3 className="font-display font-extrabold text-sm tracking-tight">{t("neon_title")}</h3>
                  <p className="text-[11px] text-neutral-500 mt-0.5">{t("neon_text")}</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              variants={reveal}
              custom={2}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="card-soft overflow-hidden"
            >
              <div className="relative h-[177px]">
                <img src={t("lounge_snacks_image") || SNACKS_IMAGE} alt="Snacks and non-alcoholic drinks on a lounge coffee table" loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute bottom-3 left-3 right-3 glass-chip rounded-xl px-4 py-2.5">
                  <h3 className="font-display font-extrabold text-sm tracking-tight">{t("snacks_title")}</h3>
                  <p className="text-[11px] text-neutral-500 mt-0.5">{t("snacks_text")}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              variants={reveal}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              className="card-soft p-6"
            >
              <span className="w-10 h-10 rounded-xl bg-[#1d1d1b]/[0.07] flex items-center justify-center">
                <f.icon size={17} className="text-[#1d1d1b]" />
              </span>
              <h4 className="font-display font-extrabold mt-4 text-sm sm:text-base tracking-tight">{f.title}</h4>
              <p className="text-xs sm:text-sm text-neutral-500 mt-2 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
